import React, {ReactElement, useEffect, useState} from 'react';
import {
  Card,
  CardContent,
  Chip,
  Typography,
  makeStyles
} from '@material-ui/core';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import HelperUtils from '@shared/utils/HelperUtils';
import {getPatientRecordChanges} from '@shared/services/PatientsServices';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import Loader from '@components/framework/loader/Loader';
import './RecordChangesCard.scss';

const useStyles = makeStyles(() => ({
  card: {
    height: '100%'
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '6rem'
  },
  createdChip: {
    backgroundColor: '#E8F1FF',
    color: '#1B6BDE',
    fontWeight: 600,
    height: 22
  },
  updatedChip: {
    backgroundColor: '#FFF4E5',
    color: '#B25E09',
    fontWeight: 600,
    height: 22
  }
}));

interface ChangedField {
  field: string;
  old: unknown;
  new: unknown;
}

interface ChangeLog {
  id: number;
  action: string;
  update_source?: string;
  created_at?: string;
  created_by?: string | null;
  changed_fields: ChangedField[];
}

interface RecordSection {
  id?: number;
  record_status?: string;
  created_at?: string;
  modified_at?: string;
  name?: string;
  changes?: ChangeLog[];
}

interface RecordChangesCardProps {
  patientId?: number | string | null;
}

function isBlankChangeValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }
  return false;
}

function formatChangeValue(value: unknown, t: (key: string) => string): string {
  if (isBlankChangeValue(value)) {
    return t('COMMON.NONE_VALUE');
  }
  if (value === '[redacted]') {
    return t('PATIENTS.RECORD_CHANGES.REDACTED');
  }
  if (value === '[changed]') {
    return t('PATIENTS.RECORD_CHANGES.CHANGED');
  }
  if (typeof value === 'boolean') {
    return value ? t('COMMON.YES') : t('COMMON.NO');
  }
  return String(value);
}

const RecordChangesCard = ({
  patientId
}: RecordChangesCardProps): ReactElement => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const groupId: number | null = useSelector(
    (state: any) => state.auth.groupId || null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [patientSection, setPatientSection] = useState<RecordSection | null>(
    null
  );
  const [
    guarantorSection,
    setGuarantorSection
  ] = useState<RecordSection | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadChanges = async (): Promise<void> => {
      if (!groupId || !patientId) {
        return;
      }
      try {
        setIsLoading(true);
        const response = await getPatientRecordChanges(groupId, patientId);
        if (cancelled) {
          return;
        }
        setPatientSection(response?.data?.patient || null);
        setGuarantorSection(response?.data?.guarantor || null);
      } catch (error: any) {
        if (!cancelled) {
          enqueueSnackbar(
            error?.message || t('PATIENTS.RECORD_CHANGES.LOAD_ERROR'),
            SnackBarConfig('e')
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    loadChanges();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar, groupId, patientId, t]);

  const fieldLabel = (field: string): string =>
    t(`PATIENTS.RECORD_CHANGES.FIELDS.${field.toUpperCase()}`, {
      defaultValue: field.replace(/_/g, ' ')
    });

  const renderStatusChip = (status?: string): ReactElement => {
    const isUpdated = status === 'updated';
    return (
      <Chip
        size="small"
        label={
          isUpdated
            ? t('PATIENTS.RECORD_CHANGES.UPDATED')
            : t('PATIENTS.RECORD_CHANGES.CREATED')
        }
        className={isUpdated ? classes.updatedChip : classes.createdChip}
      />
    );
  };

  const renderChangeRows = (log: ChangeLog): ReactElement | null => {
    const fields = (log.changed_fields || []).filter(
      item =>
        item && (!isBlankChangeValue(item.old) || !isBlankChangeValue(item.new))
    );
    if (!fields.length) {
      return null;
    }
    return (
      <div className="record-changes-card__fields">
        {fields.map(item => (
          <div
            className="record-changes-card__field"
            key={`${log.id}-${item.field}`}>
            <div className="record-changes-card__field-name">
              {fieldLabel(item.field)}
            </div>
            {log.action === 'create' ? (
              <div className="record-changes-card__field-value">
                {t('PATIENTS.RECORD_CHANGES.SET_TO')}{' '}
                <strong>{formatChangeValue(item.new, t)}</strong>
              </div>
            ) : (
              <div className="record-changes-card__field-value">
                <span className="record-changes-card__old">
                  {formatChangeValue(item.old, t)}
                </span>
                <span className="record-changes-card__arrow">→</span>
                <strong>{formatChangeValue(item.new, t)}</strong>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderSection = (
    title: string,
    section: RecordSection | null
  ): ReactElement => {
    const logs = (section?.changes || []).filter(
      log =>
        log.action === 'create' ||
        (log.changed_fields && log.changed_fields.length)
    );
    return (
      <div className="record-changes-card__section">
        <div className="record-changes-card__section-header">
          <Typography className="record-changes-card__section-title">
            {title}
          </Typography>
          {section ? renderStatusChip(section.record_status) : null}
        </div>
        {!section && (
          <div className="record-changes-card__empty">
            {t('PATIENTS.RECORD_CHANGES.NOT_AVAILABLE')}
          </div>
        )}
        {section && !logs.length && (
          <div className="record-changes-card__empty">
            {t('PATIENTS.RECORD_CHANGES.NO_HISTORY')}
            {section.created_at ? (
              <div>
                {t('PATIENTS.CREATED_ON')}:{' '}
                {HelperUtils.formatDisplayDateTime(section.created_at)}
              </div>
            ) : null}
            {section.record_status === 'updated' && section.modified_at ? (
              <div>
                {t('PATIENTS.RECORD_CHANGES.LAST_UPDATED')}:{' '}
                {HelperUtils.formatDisplayDateTime(section.modified_at)}
              </div>
            ) : null}
          </div>
        )}
        {logs.map(log => (
          <div className="record-changes-card__log" key={log.id}>
            <div className="record-changes-card__log-meta">
              <span>
                {log.created_at
                  ? HelperUtils.formatDisplayDateTime(log.created_at)
                  : t('COMMON.NONE_VALUE')}
              </span>
              {log.update_source ? <span>{log.update_source}</span> : null}
              {log.created_by ? <span>{log.created_by}</span> : null}
            </div>
            {renderChangeRows(log)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className={`record-changes-card ${classes.card}`}>
      <CardContent>
        <Typography className="card-title">
          {t('PATIENTS.RECORD_CHANGES.TITLE')}
        </Typography>
        {isLoading ? (
          <div className={classes.loader}>
            <Loader />
          </div>
        ) : (
          <div className="record-changes-card__body">
            {renderSection(
              t('PATIENTS.RECORD_CHANGES.PATIENT'),
              patientSection
            )}
            {renderSection(
              t('PATIENTS.RECORD_CHANGES.GUARANTOR'),
              guarantorSection
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecordChangesCard;
